import { memo } from "react";
import {
    BarChartConfig,
    CompositionChartConfig,
    ChartConfig,
    ChartType,
    LeagueTableChartConfig,
    TimeseriesChartConfig,
} from "~/modules/charts/types";
import { LeagueTableChartRenderer } from "./LeagueTableChartRenderer";
import { NetFlowsChartRenderer } from "./NetFlowsChartRenderer";
import { BarChartRenderer } from "./BarChartRenderer";
import { CompositionChartRenderer } from "./CompositionChartRenderer";
import { TimeseriesChartRenderer } from "./TimeseriesChartRenderer";
import { AssetTableChartRenderer } from "./AssetTableChartRenderer";

// // // //

/**
 * ChartRenderer
 * Renders the appropriate chart visualization for a given chart type.
 *
 * Memoized so it skips re-rendering (and the expensive chart/query work in the
 * type-specific renderers) when an unrelated parent state change leaves its
 * props unchanged — e.g. switching tabs in the chart form.
 */
export const ChartRenderer = memo(function ChartRenderer(props: {
    title: string;
    description: string;
    type: ChartType;
    config: ChartConfig;
    debug?: boolean;
}) {
    const { title, description, type, config } = props;

    if (type === "timeseries") {
        return (
            <TimeseriesChartRenderer
                title={title}
                description={description}
                config={config as unknown as TimeseriesChartConfig}
            />
        );
    }

    if (type === "bar_chart") {
        return (
            <BarChartRenderer
                title={title}
                description={description}
                config={config as unknown as BarChartConfig}
            />
        );
    }

    if (type === "composition") {
        return (
            <CompositionChartRenderer
                title={title}
                description={description}
                config={config as unknown as CompositionChartConfig}
            />
        );
    }

    if (type === "league_table") {
        return (
            <LeagueTableChartRenderer
                title={title}
                config={config as unknown as LeagueTableChartConfig}
            />
        );
    }

    if (type === "net_flows") {
        return (
            <NetFlowsChartRenderer
                title={title}
                description={description}
                config={config}
            />
        );
    }

    if (type === "asset_table") {
        return (
            <AssetTableChartRenderer
                title={title}
                description={description}
                config={config}
            />
        );
    }

    return null;
});
